import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { CREW_RESOURCE } from '../crew/types';
import { eventDays, MONTHS, todayInWarsaw, WEEKDAYS_SHORT } from '../events/calendar';
import { useUrlState } from '../nav/urlState';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY, type FunctionSummary } from '../people/types';
import { addDays, batchLines, releasedLines, cellActions, selfMarkAsk, type SelfMarkDetail, cellKey, cellsCount, cellView, dayMonth, daysBetween, LEGEND, monthDays, multiActions, noticeText, PAST_DAY_NOTE, personLabel, monthOf, monthRange, PEOPLE_AVAILABILITY_RESOURCE, PEOPLE_GRID_KEY, rangeTitle, rowRange, selectionBody, shiftMonth, shortDate, twoWeeks, weekStart, type BatchQuestion, type BatchResult, type CellAction, type Grid, type GridAssignment, type GridCell } from './peopleGrid';
import { Bubble, Clip, marqueeOff, marqueeOn, MissingTile, ReleaseQuestion } from './GridParts';
import { CellHistory } from './CellHistory';
import { CellMenu } from './CellMenu';
import { ConfirmManualDialog } from './ConfirmManualDialog';
import { EventPanel } from './EventPanel';
import { ReleaseDialog } from './ReleaseDialog';
import { SelfMarkDialog } from './SelfMarkDialog';
import { refreshCrew } from '../crew/refreshCrew';
import { refreshQueries } from '../refresh';
import { PeopleSettingsDialog } from './PeopleSettingsDialog';
import { QuoteAfterFill } from './StaffingPanel';
import { cellChoices, choiceKey, choiceLabel, noneReason, perPerson, placesReason, quickMode, splitFor, unionChoices, type Choice, type OpenPlace, type QuickCell } from './quickFill';
import { PeopleQuestions } from './PeopleQuestions';
import { Unsent } from './Unsent';
import { DateQuestionDialog } from '../requests/DateQuestionDialog';
import { REQUESTS_KEY, REQUESTS_RESOURCE } from '../requests/types';
import { EVENTS_RESOURCE, isClosed } from '../events/labels';
import { barColumns, barPast, barText, packBars, barTone, crewLink, type MissingDay, type StripEvent } from './staffing';
import './people-grid.css';
import { Notice } from '../ui/Notice';

type Withdrawn = { assignmentId: string; eventId?: string; eventName: string; day: string };
type Cells = Array<{ personId: string; day: string }>;
/** Owner 2026-10-27 08:14: one action on several cells (POST …/batch). */
/** `confirm` (owner 2026-10-28 12:01 „Potwierdź ręcznie”): the cells' „Proponowany” → „Potwierdzony”, with an optional note. */
/** `consulted` (owner 2026-10-29 00:29, review I1): „Tak, zmień” — the token of the people's own marks that were listed. */
type BatchBody = { action: 'available' | 'unavailable' | 'fill' | 'confirm'; cells: Cells; hours?: { from: string; to: string }; release?: boolean; asked?: Cells; eventId?: string; functionId?: string | null; note?: string; consulted?: string };
/**
 * `keep` (review M5): an action on one cell outside the chosen ones leaves the choice as it is. Owner 12:37 („Obsadź”):
 * `none` — the cells that place does not fit (said in the result), `filled` — „Wesele A – Technik” for „Obsadzono: …”.
 */
type BatchRun = { body: BatchBody; keep: boolean; none?: string[]; filled?: string };
/** The parts of the page a click may hit without clearing the chosen cells (owner 08:23: „poza oknem” clears them). */
const KEEPS_SELECTION = '.people-grid-scroll, .notice-area, .people-grid-actions, .pg-menu, .pg-side, .dialog-backdrop, [role="dialog"], [role="alertdialog"]';

const monthTitle = (month: string) => `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const weekday = (day: string) => WEEKDAYS_SHORT[(new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7]!;

/**
 * „Dostępność → Ludzie” (spec 2026-10-25, stage 1; owner 2026-10-26 07:49 „the full staffing place”): people as rows,
 * the days of two weeks or a month as columns; above them the events strip (a bar per event over its days, filled /
 * needed places per function, green when complete and confirmed); a cell shows one status (see `cellView`, legend
 * under the grid) and its click opens one menu (`cellActions`); the „dni” column counts the active working days.
 * Shift / Ctrl-click chooses several cells for „Dostępny / Niedostępny / Wyczyść” or „Wyślij zapytanie” at once.
 * Owner 2026-10-27 08:14–08:23: Alt / Option-click chooses cells too; the menu of a chosen cell then applies „Dostępny”,
 * „Niedostępny” (asking first about staffed days) and „Obsadź…” to every chosen cell in one call; a click outside the grid
 * or Esc clears the choice. Tiles are fixed squares (D / N for the marks), bars span exactly their days with the numbers
 * in a hover bubble, and the unlabelled „Brakuje” row under the strip counts the free places per day.
 */
export function PeoplePanel() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const today = todayInWarsaw();
  // owner 2026-10-26 07:49: a range of two weeks (from a Monday) or a month
  const twoWeekMode = params.get('zakres') === '2t';
  const month = /^\d{4}-\d{2}$/.test(params.get('miesiac') ?? '') ? params.get('miesiac')! : monthOf(today);
  const start = DAY_RE.test(params.get('od') ?? '') ? weekStart(params.get('od')!) : weekStart(today);
  const { from, to } = twoWeekMode ? twoWeeks(start) : monthRange(month);
  const functionId = params.get('funkcja') ?? '';
  const chosen = (params.get('osoby') ?? '').split(',').filter(Boolean);
  const onlyFree = params.get('wolni') === '1';
  const onlyMissing = params.get('braki') === '1';
  // owner 2026-10-26 07:49, point 2: the event chosen on the strip — its side panel
  const chosenEvent = params.get('wydarzenie');
  const query = `from=${from}&to=${to}${functionId ? `&functionId=${functionId}` : ''}${chosen.length > 0 ? `&personIds=${chosen.join(',')}` : ''}${onlyFree ? '&free=1' : ''}`;
  const grid = useQuery({ queryKey: [...PEOPLE_GRID_KEY, query], placeholderData: keepPreviousData, queryFn: () => api<Grid>(`/api/crew/people-availability?${query}`) });
  const stripQuery = `from=${from}&to=${to}${functionId ? `&functionId=${functionId}` : ''}${onlyMissing ? '&missing=1' : ''}`;
  const strip = useQuery({ queryKey: [...PEOPLE_GRID_KEY, 'events', stripQuery], placeholderData: keepPreviousData, queryFn: () => api<{ events: StripEvent[]; missing?: MissingDay[] }>(`/api/crew/people-availability/events?${stripQuery}`) });
  // the people to choose from: everyone (one day is enough to get the list)
  const everyone = useQuery({ queryKey: [...PEOPLE_GRID_KEY, 'people'], queryFn: () => api<Grid>(`/api/crew/people-availability?from=${from}&to=${from}`) });
  const mayFunctions = !!me && can(me, FUNCTIONS_RESOURCE, 'view');
  const functions = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled: mayFunctions, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const last = useRef<{ personId: string; day: string } | null>(null);
  // owner 2026-10-28 00:19: the cell of the last plain click (its menu open or closed) — the next Shift / Ctrl / ⌘ /
  // Alt-click adds it to the choice too, so „A, then ⌘ on B” chooses A and B (it was only B: A never joined the set)
  const active = useRef<{ personId: string; day: string } | null>(null);
  const clearSelection = () => {
    active.current = null;
    setSelected(new Set());
  };
  const [done, setDone] = useState<{ changed: number; withdrawn: Withdrawn[] } | null>(null);
  // stage 1b A: a time range instead of the whole day
  const [withHours, setWithHours] = useState(false);
  const [hours, setHours] = useState({ from: '08:00', to: '16:00' });
  // stage 1b I: „Historia” of a cell (from its menu)
  const [focus, setFocus] = useState<{ personId: string; day: string } | null>(null);
  // owner 2026-10-26 07:49: the menu of the clicked cell
  const [menu, setMenu] = useState<{ personId: string; day: string; anchor: HTMLElement } | null>(null);
  const menuOpen = !!menu;
  // owner 2026-10-26 07:42 „Usuń ze stanowiska” (from the cell menu)
  const [releasing, setReleasing] = useState<{ personId: string; day: string; assignment: GridAssignment } | null>(null);
  const [released, setReleased] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // owner 2026-10-27 08:14: the question „Wyrzucić z obsady?”, the „Obsadź…” of several cells, what came out
  const [releaseAsk, setReleaseAsk] = useState<{ run: BatchRun; question: BatchQuestion[] } | null>(null);
  const [outcome, setOutcome] = useState<{ done: number; total: number; lines: string[]; released: string[]; filled?: string } | null>(null);
  // owner 2026-10-27 12:37 („obsadź to obsadź”): the places of „Obsadź” to choose from (several), or none that day
  const [quick, setQuick] = useState<{ cells: QuickCell[]; choices: Choice[]; keep: boolean } | null>(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const [noPlace, setNoPlace] = useState<{ days: string[]; reason?: string } | null>(null);
  const quickToken = useRef(0);
  const [quickError, setQuickError] = useState<string | null>(null);
  // review M1: after „Obsadź…” of several cells, the event's quote question as after one fill
  const [quoteFor, setQuoteFor] = useState<{ eventId: string; n: number } | null>(null);
  // owner 08:21: the „Brakuje” day whose list of events is open
  const [missOpen, setMissOpen] = useState<string | null>(null);
  // owner 2026-10-28 12:01 „Potwierdź ręcznie”: the cells (and the event of one cell's item) waiting for the confirm step
  const [confirming, setConfirming] = useState<{ cells: Cells; eventId?: string; subject: string; keep: boolean } | null>(null);
  // owner 2026-10-29 00:29: the people's own marks a change would overwrite — „Tak, zmień” sends it again with `consulted`
  const [selfAsk, setSelfAsk] = useState<{ marks: SelfMarkDetail[]; retry: () => void } | null>(null);
  // staffing from the grid: „Obsadź” of a person's cell (owner 12:37 — straight from the menu)
  const mayStaff = !!me && can(me, CREW_RESOURCE, 'create') && fieldVisible(me, CREW_RESOURCE, 'personId') && fieldVisible(me, CREW_RESOURCE, 'functionId');
  // stage 2: „Wyślij zapytanie” for the selected people and days, or one cell (an event optional), sent by hand
  const mayAsk = !!me && can(me, REQUESTS_RESOURCE, 'create');
  const mayQuestions = !!me && can(me, REQUESTS_RESOURCE, 'view');
  // owner 12:32 B: „Obsadź” on a day with no mark asks first — pinned to the event (and the place of one cell)
  const [asking, setAsking] = useState<{ personIds: string[]; days: string[]; perPerson?: Array<{ personId: string; days: string[] }>; event?: { id: string; name: string }; functionId?: string; positionId?: string } | null>(null);
  const askDays = [...new Set(selectionBody(selected).map((c) => c.day))].sort().filter((d) => d >= today);
  const mayEvents = !!me && can(me, EVENTS_RESOURCE, 'view');
  const dayEvents = useQuery({
    queryKey: ['events', 'for-question', asking?.days[0], asking?.days.at(-1)],
    enabled: !!asking && mayEvents && asking.days.length > 0,
    queryFn: () => api<{ events: Array<{ id: string; name?: string; status?: string; startsAt?: string; endsAt?: string }> }>(`/api/events?from=${asking!.days[0]}&to=${asking!.days.at(-1)}`),
  });
  // every change here may change a crew or who can be picked (review I3: a mark greys a person out in the open
  // panel's pickers): Obsada, the panel, the grid and the strip read again
  const refresh = () => refreshCrew(queryClient);
  const mark = useMutation({
    mutationFn: ({ state, cells: list, consulted }: { state: 'available' | 'unavailable' | null; cells?: Array<{ personId: string; day: string }>; consulted?: string }) =>
      api<{ changed: number; withdrawn: Withdrawn[] }>('/api/crew/people-availability', { method: 'POST', body: { cells: list ?? selectionBody(selected), state, ...(!list && withHours && state !== null ? { hours } : {}), ...(consulted ? { consulted } : {}) } }),
    onError: (e, v) => {
      const ask = selfMarkAsk(e);
      if (ask) setSelfAsk({ marks: ask.marks, retry: () => mark.mutate({ ...v, consulted: ask.token }) });
    },
    onSuccess: async (result, v) => {
      setDone(result);
      if (!v.cells) clearSelection();
      // a withdrawal changed crews and the calendar's „nieobsadzone”
      await refresh();
    },
  });
  // „Wymuś tutaj”: the person does this event that day, the other one becomes inactive (stage 1b D)
  const force = useMutation({
    mutationFn: ({ assignmentId, day }: { assignmentId: string; day: string }) => api(`/api/crew/assignments/${assignmentId}/force`, { method: 'POST', body: { days: [day] } }),
    onSuccess: async () => {
      setDone({ changed: 1, withdrawn: [] });
      await refresh();
    },
  });
  // owner 2026-10-27 08:14: „Dostępny”, „Niedostępny” (asked first about staffed days), „Obsadź…” on one or several cells
  const runBatch = useMutation({
    mutationFn: ({ body }: BatchRun) => api<{ results?: BatchResult[]; question?: BatchQuestion[] }>('/api/crew/people-availability/batch', { method: 'POST', body }),
    // owner 00:29: first the question about the people's own marks; „Tak, zmień” — the same again, consulted (a later
    // „Wyrzucić z obsady?” keeps it)
    onError: (e, run) => {
      const ask = selfMarkAsk(e);
      if (ask) setSelfAsk({ marks: ask.marks, retry: () => runBatch.mutate({ ...run, body: { ...run.body, consulted: ask.token } }) });
    },
    onSuccess: async (r, run) => {
      // a question (again, review I2: the staffed cells changed meanwhile)
      if (r.question) {
        setReleaseAsk({ run, question: r.question });
        return;
      }
      const results = r.results ?? [];
      const label = (id: string) => personLabel(grid.data?.people.find((p) => p.id === id) ?? {});
      setReleaseAsk(null);
      const lines = [...batchLines(results, label), ...(run.none ?? [])];
      const done = results.filter((x) => x.result === 'done').length;
      setOutcome({ done, total: results.length + (run.none ?? []).length, lines, released: releasedLines(results, label), ...(run.filled && lines.length === 0 && done > 0 ? { filled: run.filled } : {}) });
      if (run.body.action === 'fill' && run.body.eventId && results.some((x) => x.result === 'done')) setQuoteFor({ eventId: run.body.eventId, n: Date.now() });
      // the selection clears after an action on it (review M5: not after one on a cell outside it)
      if (!run.keep) clearSelection();
      await refresh();
    },
  });
  // owner 2026-10-29 00:32: „Zrezygnuj z przydziału” — the grid, the lists of questions and „Mój kalendarz” read again
  const dismissAssign = useMutation({
    mutationFn: ({ questionId }: { questionId: string; text: string }) => api(`/api/requests/to-assign/${questionId}/dismiss`, { method: 'POST' }),
    onSuccess: async (_r, v) => {
      setReleased(v.text);
      await Promise.all([refresh(), refreshQueries(queryClient, REQUESTS_KEY)]);
    },
  });
  // a hook — before the early return below (it rendered more hooks once „me” arrived)
  const dismiss = useMutation({
    mutationFn: (id: string) => api(`/api/crew/people-availability/notices/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => refreshQueries(queryClient, PEOPLE_GRID_KEY),
  });
  // Esc clears the selection — owner 2026-10-28 (variant A): with the event's drawer open, Esc closes only the drawer
  // (its own layer) and the chosen cells stay chosen; the next Esc clears them
  const drawerOpen = !!chosenEvent && !!me && can(me, CREW_RESOURCE, 'view');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Esc that closes a cell's menu keeps its cell for the next modifier-click (the menu goes, the cell stays chosen);
      // Esc that closes the event's drawer keeps the chosen cells and that cell too (variant A)
      if (!menuOpen && !drawerOpen) active.current = null;
      if (selected.size > 0 && !drawerOpen) setSelected(new Set());
    };
    // owner 2026-10-27 08:23: a click outside the grid (its toolbar, menu, dialogs and side panel are inside) clears it
    const onDown = (e: MouseEvent) => {
      if (e.target instanceof Element && e.target.closest(KEEPS_SELECTION)) return;
      active.current = null;
      if (selected.size > 0) setSelected(new Set());
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [selected.size, menuOpen, drawerOpen]);
  // owner 2026-10-27 22:28: a month is wider than the window and opened at the 1st — in the current month only past days
  // (no „Obsadź”, no „Wyślij zapytanie”) were in view. A new range opens at today when it has it, else at its first day.
  const scroller = useRef<HTMLDivElement>(null);
  const openedAt = useRef<string | null>(null);
  const shownRange = grid.data?.days ? `${grid.data.days[0]}:${grid.data.days.at(-1)}` : null;
  useLayoutEffect(() => {
    const box = scroller.current;
    if (!box || shownRange !== `${from}:${to}` || openedAt.current === shownRange) return;
    openedAt.current = shownRange;
    const column = box.querySelector<HTMLElement>(`thead th[data-day="${today}"]`);
    const names = box.querySelector<HTMLElement>('thead .people-grid-person');
    box.scrollLeft = column && names ? column.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft - names.offsetWidth : 0;
  });
  if (!me) return null;
  const mayEdit = can(me, PEOPLE_AVAILABILITY_RESOURCE, 'edit') && fieldVisible(me, PEOPLE_AVAILABILITY_RESOURCE, 'state');
  // „Wymuś tutaj” and (owner 2026-10-28 12:01) „Potwierdź ręcznie”: „Obsada: Edycja” with the field „Status”
  const mayForce = can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'status');
  // as a release in Obsada: „Obsada: Usuwanie” and the field „Osoba”
  const mayRelease = can(me, CREW_RESOURCE, 'delete') && fieldVisible(me, CREW_RESOURCE, 'personId');
  const days = grid.data?.days ?? (twoWeekMode ? daysBetween(from, to) : monthDays(month));
  const cells = new Map((grid.data?.cells ?? []).map((c) => [cellKey(c.personId, c.day), c]));
  const bars = (strip.data?.events ?? []).flatMap((e, i) => {
    const cols = barColumns(e, days);
    return cols ? [{ e, cols, key: e.eventId ?? `inne-${i}` }] : [];
  });
  const title = twoWeekMode ? rangeTitle(from, to) : monthTitle(month);
  // review M2: the panel by the event's id — it stays when a filter hides the bar; needs „Obsada: Podgląd”
  const sideEvent = chosenEvent && can(me, CREW_RESOURCE, 'view') ? chosenEvent : null;
  const choose = (eventId: string | null) => {
    setMenu(null);
    setMissOpen(null);
    update({ wydarzenie: eventId });
  };
  const current = twoWeekMode ? start === weekStart(today) : month === monthOf(today);
  const go = (changes: Record<string, string | null>) => {
    clearSelection();
    setDone(null);
    setFocus(null);
    setMenu(null);
    setQuick(null);
    setNoPlace(null);
    setQuickError(null);
    setReleased(null);
    setOutcome(null);
    setMissOpen(null);
    setSelfAsk(null);
    update(changes);
  };
  const shift = (delta: number) => (twoWeekMode ? go({ od: addDays(start, 14 * delta) }) : go({ miesiac: shiftMonth(month, delta) }));
  const nameOf = personLabel;
  // owner 08:14: the menu of a cell among several chosen ones acts on all of them
  const multi = !!menu && selected.size > 1 && selected.has(cellKey(menu.personId, menu.day));

  /** A click opens the cell's menu; Shift (a row's range) or Ctrl / ⌘ / Alt-Option (one more cell) chooses cells instead. */
  function pick(personId: string, day: string, how: { shift: boolean; add: boolean }, anchor: HTMLElement) {
    // review I2: another cell — a pending „Obsadź” answer is stale
    quickToken.current += 1;
    setQuickBusy(false);
    setQuick(null);
    if (!how.shift && !how.add) {
      setMenu({ personId, day, anchor });
      last.current = { personId, day };
      active.current = { personId, day };
      return;
    }
    const plain = active.current;
    active.current = null;
    setMenu(null);
    if (!mayEdit && !mayAsk && !mayStaff && !mayForce) return;
    setDone(null);
    setOutcome(null);
    const next = new Set(selected);
    const from = last.current;
    if (how.shift && from && from.personId === personId) for (const d of rowRange(days, from.day, day)) next.add(cellKey(personId, d));
    else if (next.has(cellKey(personId, day))) next.delete(cellKey(personId, day));
    else next.add(cellKey(personId, day));
    // owner 00:19: the plain-clicked cell joins (a modifier-click on that very cell only toggles it)
    if (plain && cellKey(plain.personId, plain.day) !== cellKey(personId, day)) next.add(cellKey(plain.personId, plain.day));
    last.current = { personId, day };
    setSelected(next);
  }

  /**
   * Owner 2026-10-27 12:37: „Obsadź” — per cell the free places of the person that day (`open-day`): none — „Brak wolnych
   * stanowisk w tym dniu”; one — at once; several — a tiny choice under the menu item (the menu stays open).
   */
  async function startQuick(list: Cells, keep: boolean) {
    // review I2: only the newest „Obsadź” counts — a closed menu or a newer one makes this answer stale
    const token = ++quickToken.current;
    setQuickBusy(true);
    setQuick(null);
    try {
      const found: QuickCell[] = await Promise.all(
        list.map(async (c): Promise<QuickCell> => {
          const mode = quickMode(cells.get(cellKey(c.personId, c.day)));
          // review M5: the reason a cell gets nothing
          if (c.day < today) return { ...c, mode, choices: [], reason: 'dzień minął' };
          // a day with no mark asks first: without the right to ask, nothing fits
          if (mode === 'ask' && !mayAsk) return { ...c, mode, choices: [], reason: 'bez oznaczenia, a brak prawa do wysyłania zapytań' };
          const panel = await api<{ places: OpenPlace[] }>(`/api/crew/people-availability/open-day?day=${c.day}&personId=${c.personId}`);
          const choices = cellChoices(panel.places);
          const reason = choices.length === 0 ? placesReason(panel.places) : undefined;
          return { ...c, mode, choices, ...(reason ? { reason } : {}) };
        }),
      );
      if (token !== quickToken.current) return;
      const choices = unionChoices(found);
      if (choices.length === 1) {
        setMenu(null);
        place(choices[0]!, found, keep);
      } else if (choices.length === 0) {
        setMenu(null);
        const reasons = [...new Set(found.map((c) => c.reason ?? ''))];
        setNoPlace({ days: [...new Set(list.map((c) => c.day))].sort(), ...(reasons.length === 1 && reasons[0] ? { reason: reasons[0] } : {}) });
      } else setQuick({ cells: found, choices, keep });
    } catch (e) {
      if (token !== quickToken.current) return;
      setMenu(null);
      setQuickError(e instanceof ApiError ? e.message : 'Nie udało się sprawdzić wolnych stanowisk');
    } finally {
      if (token === quickToken.current) setQuickBusy(false);
    }
  }

  /** Review I2: closing the menu (or picking) makes a pending „Obsadź” answer stale. */
  function closeMenu() {
    quickToken.current += 1;
    setQuickBusy(false);
    setQuick(null);
    setMenu(null);
  }

  /** One place chosen: the days „Dostępny” are staffed (the batch fill — „Proponowany”), the days with no mark asked. */
  function place(choice: Choice, found: readonly QuickCell[], keep: boolean) {
    quickToken.current += 1;
    setQuick(null);
    const { fill, ask, none } = splitFor(choice, found);
    const noneLines = none.map((c) => `${nameById(c.personId)} ${dayMonth(c.day)} — ${noneReason(found.find((x) => x.personId === c.personId && x.day === c.day)!, choice)}`);
    if (fill.length > 0) runBatch.mutate({ body: { action: 'fill', cells: fill, eventId: choice.eventId, functionId: choice.functionId }, keep, none: noneLines, filled: choiceLabel(choice) });
    else if (noneLines.length > 0) setOutcome({ done: 0, total: none.length, lines: noneLines, released: [] });
    if (ask.length > 0) {
      const one = ask.length === 1 ? found.find((c) => c.personId === ask[0]!.personId && c.day === ask[0]!.day)?.choices.find((x) => choiceKey(x) === choiceKey(choice)) : undefined;
      // review I1: each person asked only about their own days
      const groups = perPerson(ask);
      setAsking({
        personIds: groups.map((g) => g.personId),
        days: [...new Set(ask.map((c) => c.day))].sort(),
        perPerson: groups,
        event: { id: choice.eventId, name: choice.eventName },
        functionId: choice.functionId,
        ...(one?.positionId ? { positionId: one.positionId } : {}),
      });
    }
  }

  function act(a: CellAction) {
    if (!menu) return;
    // an action on the menu's cell is done with it: the next modifier-click starts from the chosen cells only
    active.current = null;
    const { personId, day } = menu;
    setDone(null);
    setReleased(null);
    setOutcome(null);
    setNoPlace(null);
    setQuickError(null);
    // owner 12:37: „Obsadź” keeps the menu open while it looks (several places: the choice is shown under the item)
    if (a.id === 'fill') {
      void startQuick(multi ? selectionBody(selected) : [{ personId, day }], multi ? false : !selected.has(cellKey(personId, day)));
      return;
    }
    setMenu(null);
    // owner 2026-10-27 08:14: the menu of a chosen cell acts on every chosen cell
    if (multi) {
      const list = selectionBody(selected);
      if (a.id === 'available' || a.id === 'unavailable') runBatch.mutate({ body: { action: a.id, cells: list }, keep: false });
      // owner 2026-10-28 12:01: every chosen cell where it fits — the skipped ones are named in the result
      if (a.id === 'confirm') setConfirming({ cells: list, subject: `Zaznaczone: ${cellsCount(list.length)}`, keep: false });
      return;
    }
    const assignment = cells.get(cellKey(personId, day))?.assignments?.find((x) => x.assignmentId === a.assignmentId);
    if (a.id === 'confirm' && assignment) {
      const who = nameOf(grid.data?.people.find((p) => p.id === personId) ?? {});
      setConfirming({ cells: [{ personId, day }], ...(assignment.eventId ? { eventId: assignment.eventId } : {}), subject: `${who} · ${assignment.eventName} · ${shortDate(day)}`, keep: !selected.has(cellKey(personId, day)) });
      return;
    }
    if (a.id === 'dismiss' && a.questionId) {
      const waiting = cells.get(cellKey(personId, day))?.toAssign?.find((t) => t.questionId === a.questionId);
      dismissAssign.mutate({ questionId: a.questionId, text: `${nameOf(grid.data?.people.find((p) => p.id === personId) ?? {})}: zrezygnowano z przydziału ręcznego${waiting ? ` — ${waiting.eventName}` : ''}. „Obsadź” nadal działa.` });
      return;
    }
    if (a.id === 'release' && assignment) setReleasing({ personId, day, assignment });
    else if (a.id === 'force') force.mutate({ assignmentId: a.assignmentId!, day });
    else if (a.id === 'available' || a.id === 'unavailable') runBatch.mutate({ body: { action: a.id, cells: [{ personId, day }] }, keep: !selected.has(cellKey(personId, day)) });
    else if (a.id === 'clear') mark.mutate({ state: null, cells: [{ personId, day }] });
    else if (a.id === 'ask') setAsking({ personIds: [personId], days: [day] });
    else setFocus({ personId, day });
  }

  // owner 00:29: `self_mark` is a question (the dialog), not an error
  const error = [mark.error, force.error, runBatch.error, dismissAssign.error].find((e) => e instanceof ApiError && !selfMarkAsk(e)) as ApiError | undefined;
  const menuCell = menu ? cells.get(cellKey(menu.personId, menu.day)) : undefined;
  const nameById = (id: string) => nameOf(grid.data?.people.find((p) => p.id === id) ?? {});
  const missing = new Map((strip.data?.missing ?? []).map((m) => [m.day, m]));
  const menuEvent = menuCell?.assignments?.find((a) => a.active !== false) ?? menuCell?.assignments?.[0];
  // owner 12:37: „Zmień zapotrzebowanie” — the numbers of ⓘ Info (Obsada: Edycja, „Funkcja”) in the event's panel (Obsada: Podgląd)
  const mayCounts = can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'functionId') && can(me, CREW_RESOURCE, 'view');
  /** The events of the strip on those days whose panel may be opened. */
  const eventsOn = (list: readonly string[]) => (strip.data?.events ?? []).flatMap((e) => (e.eventId && e.functions && list.some((d) => d >= e.firstDay && d <= e.lastDay) ? [{ eventId: e.eventId, eventName: e.eventName }] : []));
  return (
    <div className="people-grid-panel" data-feature-id="crew.people_availability">
      <div className="people-grid-bar">
        <div className="people-grid-month">
          <button className="btn ghost small" type="button" aria-label={twoWeekMode ? 'Poprzednie dwa tygodnie' : 'Poprzedni miesiąc'} onClick={() => shift(-1)}>
            ‹
          </button>
          <h2 aria-live="polite">{title}</h2>
          <button className="btn ghost small" type="button" aria-label={twoWeekMode ? 'Następne dwa tygodnie' : 'Następny miesiąc'} onClick={() => shift(1)}>
            ›
          </button>
          {!current && (
            <button className="btn ghost small" type="button" onClick={() => go({ miesiac: null, od: null })}>
              Dziś
            </button>
          )}
          <div className="pg-range" role="group" aria-label="Zakres">
            <button className={`btn small${twoWeekMode ? ' pg-on' : ' ghost'}`} type="button" aria-pressed={twoWeekMode} onClick={() => go({ zakres: '2t', miesiac: null, od: twoWeekMode ? start : weekStart(month === monthOf(today) ? today : `${month}-01`) })}>
              2 tygodnie
            </button>
            <button className={`btn small${twoWeekMode ? ' ghost' : ' pg-on'}`} type="button" aria-pressed={!twoWeekMode} onClick={() => go({ zakres: null, od: null, miesiac: twoWeekMode && monthOf(start) !== monthOf(today) ? monthOf(start) : null })}>
              Miesiąc
            </button>
          </div>
        </div>
        <div className="filters">
          <label className="check">
            <input type="checkbox" checked={onlyMissing} onChange={(e) => go({ braki: e.target.checked ? '1' : null })} />
            Tylko wydarzenia z brakami
          </label>
          <label className="check">
            <input type="checkbox" checked={onlyFree} onChange={(e) => go({ wolni: e.target.checked ? '1' : null })} />
            Tylko wolni
          </label>
          {mayFunctions && (
            <label className="field">
              <span>Funkcja</span>
              <select value={functionId} onChange={(e) => go({ funkcja: e.target.value || null })}>
                <option value="">Wszystkie funkcje</option>
                {(functions.data?.functions ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <PeopleChooser people={everyone.data?.people ?? []} chosen={chosen} onChange={(ids) => go({ osoby: ids.length > 0 ? ids.join(',') : null })} />
          <button className="btn ghost small" type="button" aria-label="Ustawienia dostępności ludzi" title="Ustawienia" onClick={() => setSettingsOpen(true)}>
            ⚙
          </button>
        </div>
      </div>

      {/* owner 2026-10-27 12:41 / 14:08: „Do wysłania: N” over the grid, and the question before leaving */}
      <Unsent onOpenPanel={(eventId) => choose(eventId)} />
      {(mayEdit || mayAsk) && selected.size > 0 && (
        <div className="people-grid-actions" role="toolbar" aria-label="Zaznaczone dni">
          <span className="muted">
            Zaznaczone: <strong>{selected.size}</strong>
          </span>
          {mayEdit && (
            <>
              {/* review I3: through the batch — „Niedostępny” on a staffed day asks „Wyrzucić z obsady?” here too */}
              <button className="btn small" type="button" disabled={runBatch.isPending} onClick={() => runBatch.mutate({ body: { action: 'available', cells: selectionBody(selected), ...(withHours ? { hours } : {}) }, keep: false })}>
                Dostępny
              </button>
              <button className="btn small" type="button" disabled={runBatch.isPending} onClick={() => runBatch.mutate({ body: { action: 'unavailable', cells: selectionBody(selected), ...(withHours ? { hours } : {}) }, keep: false })}>
                Niedostępny
              </button>
              <button className="btn small" type="button" disabled={mark.isPending} onClick={() => mark.mutate({ state: null })}>
                Wyczyść
              </button>
              <label className="check pg-hours">
                <input type="checkbox" checked={withHours} onChange={(e) => setWithHours(e.target.checked)} />
                Godziny
              </label>
              {withHours && (
                <>
                  <input className="pg-time" type="time" aria-label="Od godziny" value={hours.from} onChange={(e) => setHours({ ...hours, from: e.target.value })} />
                  <span aria-hidden="true">–</span>
                  <input className="pg-time" type="time" aria-label="Do godziny" value={hours.to} onChange={(e) => setHours({ ...hours, to: e.target.value })} />
                </>
              )}
            </>
          )}
          {mayAsk && (
            <button className="btn small" type="button" data-feature-id="requests.dates" disabled={askDays.length === 0} title={askDays.length === 0 ? 'Zaznaczone dni minęły' : undefined} onClick={() => setAsking({ personIds: [...new Set(selectionBody(selected).map((c) => c.personId))], days: askDays })}>
              Wyślij zapytanie
            </button>
          )}
          <button className="btn ghost small" type="button" onClick={() => clearSelection()}>
            Odznacz
          </button>
        </div>
      )}
      {error && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      {done && (
        <Notice onClose={() => setDone(null)}>
          Zapisano.
          {done.withdrawn.length > 0 && (
            <ul className="people-grid-withdrawn">
              {done.withdrawn.map((w) => (
                <li key={w.assignmentId}>
                  {w.eventId ? <Link to={`/wydarzenia?wydarzenie=${w.eventId}&zakladka=obsada`}>{w.eventName}</Link> : w.eventName} ({shortDate(w.day)}): przydział przeszedł na „Odmowa” — wydarzenie nieobsadzone.
                </li>
              ))}
            </ul>
          )}
        </Notice>
      )}
      {grid.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {grid.error && (
        <p className="alert" role="alert">
          {grid.error instanceof ApiError ? grid.error.message : 'Nie udało się wczytać dostępności ludzi'}
        </p>
      )}
      {grid.data && grid.data.people.length === 0 && <p className="muted">Brak osób do pokazania.</p>}
      {(grid.data?.notices ?? []).length > 0 && (
        // 16:52 / 16:53: an event deleted or cancelled released these people — nothing was sent to them
        <ul className="pg-notices" aria-label="Do powiadomienia" data-feature-id="crew.people_availability">
          {grid.data!.notices!.map((n) => (
            <li key={n.id}>
              {noticeText(n, n.personName ?? personLabel(grid.data!.people.find((p) => p.id === n.personId) ?? {}))}
              {mayEdit && (
                <button className="btn ghost small" type="button" disabled={dismiss.isPending} onClick={() => dismiss.mutate(n.id)}>
                  Odhacz
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {released && (
        <Notice onClose={() => setReleased(null)}>
          {released}
        </Notice>
      )}
      {quickError && (
        <p className="alert" role="alert">
          {quickError}
        </p>
      )}
      {noPlace && (
        // owner 12:37: nothing changes; „Zmień zapotrzebowanie” opens the event's panel (ⓘ Info) for whoever may change the numbers
        <Notice aria-label="Obsadź" onClose={() => setNoPlace(null)}>
          {noPlace.reason ? `${noPlace.reason[0]!.toUpperCase()}${noPlace.reason.slice(1)}.` : 'Brak wolnych stanowisk w tym dniu.'}
          {mayCounts &&
            eventsOn(noPlace.days).map((e, _i, all) => (
              <button key={e.eventId} className="btn ghost small" type="button" onClick={() => choose(e.eventId)}>
                {all.length > 1 ? `Zmień zapotrzebowanie: ${e.eventName}` : 'Zmień zapotrzebowanie'}
              </button>
            ))}
        </Notice>
      )}
      {outcome && (
        <Notice aria-label="Wynik" onClose={() => setOutcome(null)}>
          {outcome.filled ? `Obsadzono: ${outcome.filled}.` : outcome.lines.length === 0 ? 'Zapisano.' : `Zapisano: ${outcome.done} z ${outcome.total}.`}
          {outcome.released.length > 0 && ` Zdjęto z obsady: ${outcome.released.join(', ')}.`}
          {outcome.lines.length > 0 && (
            <>
              {' '}
              Pominięto:
              <ul className="people-grid-withdrawn">
                {outcome.lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </>
          )}
        </Notice>
      )}
      {releaseAsk && (
        <ReleaseQuestion
          question={releaseAsk.question}
          nameOf={nameById}
          busy={runBatch.isPending}
          // review I2: the answer names the cells it answers
          onAnswer={(release) => runBatch.mutate({ ...releaseAsk.run, body: { ...releaseAsk.run.body, release, asked: releaseAsk.question.map((q) => ({ personId: q.personId, day: q.day })) } })}
          onClose={() => setReleaseAsk(null)}
        />
      )}
      {selfAsk && (
        <SelfMarkDialog
          marks={selfAsk.marks}
          nameOf={nameById}
          busy={runBatch.isPending || mark.isPending}
          onConfirm={() => {
            const { retry } = selfAsk;
            setSelfAsk(null);
            retry();
          }}
          onClose={() => setSelfAsk(null)}
        />
      )}
      {confirming && (
        <ConfirmManualDialog
          subject={confirming.subject}
          busy={runBatch.isPending}
          onConfirm={(note) => {
            runBatch.mutate({ body: { action: 'confirm', cells: confirming.cells, ...(confirming.eventId ? { eventId: confirming.eventId } : {}), ...(note ? { note } : {}) }, keep: confirming.keep });
            setConfirming(null);
          }}
          onClose={() => setConfirming(null)}
        />
      )}
      {releasing && grid.data && (
        <ReleaseDialog
          personName={nameOf(grid.data.people.find((p) => p.id === releasing.personId) ?? {})}
          day={releasing.day}
          assignment={releasing.assignment}
          onDone={setReleased}
          onClose={() => setReleasing(null)}
        />
      )}
      {focus && grid.data && <CellHistory key={`${focus.personId}:${focus.day}`} personId={focus.personId} personName={nameOf(grid.data.people.find((p) => p.id === focus.personId) ?? {})} day={focus.day} onClose={() => setFocus(null)} />}
      {quoteFor && <QuoteAfterFill key={`${quoteFor.eventId}:${quoteFor.n}`} eventId={quoteFor.eventId} />}
      {settingsOpen && <PeopleSettingsDialog editable={me.isOwner} onClose={() => setSettingsOpen(false)} />}
      {asking && grid.data && (
        <DateQuestionDialog
          title="Zapytanie o dostępność"
          // owner 12:26: listed by name before sending; a hidden name (K): how many (rule 7a)
          {...(asking.perPerson
            ? { perPerson: asking.perPerson.map((g) => ({ ...g, name: grid.data!.people.find((p) => p.id === g.personId)?.name })) }
            : { people: asking.personIds.map((id) => ({ personId: id, name: grid.data!.people.find((p) => p.id === id)?.name })), days: asking.days })}
          {...(asking.event
            ? { event: asking.event, ...(asking.functionId ? { functionId: asking.functionId } : {}), ...(asking.positionId ? { positionId: asking.positionId } : {}) }
            : {
                // review: an attached event must have every asked day (the server refuses otherwise)
                events: (dayEvents.data?.events ?? [])
                  .filter((e) => !isClosed(e.status) && e.startsAt && e.endsAt && asking.days.every((d) => d >= eventDays({ startsAt: e.startsAt!, endsAt: e.endsAt! }).first && d <= eventDays({ startsAt: e.startsAt!, endsAt: e.endsAt! }).last))
                  .map((e) => ({ id: e.id, name: e.name ?? 'Wydarzenie' })),
              })}
          onClose={() => setAsking(null)}
          onSent={() => clearSelection()}
        />
      )}
      {/* owner 2026-10-28 (variant A): the event's panel is a drawer OVER the grid — the grid keeps its full width */}
      <div className="pg-layout">
      {grid.data && grid.data.people.length > 0 && (
        <div className="people-grid-scroll" ref={scroller}>
          {/* owner 2026-10-27 08:16: fixed widths for the whole grid — nothing resizes with its content */}
          <table className="people-grid" style={{ '--pg-days': days.length } as React.CSSProperties} aria-label={`Dostępność ludzi: ${title}`} aria-multiselectable={mayEdit || mayAsk || mayStaff || mayForce || undefined}>
            <colgroup>
              <col className="pg-col-person" />
              {days.map((d) => (
                <col key={d} className="pg-col-day" />
              ))}
              <col className="pg-col-load" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="people-grid-person">
                  Osoba
                </th>
                {days.map((d) => (
                  <th key={d} scope="col" data-day={d} className={`${d === today ? 'today' : ''}${d < today ? ' pg-past' : ''}${[5, 6].includes((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7) ? ' weekend' : ''}`}>
                    <span className="people-grid-wd">{weekday(d)}</span>
                    <span className="people-grid-n">{Number(d.slice(8))}</span>
                  </th>
                ))}
                <th scope="col" className="pg-load" title="Dni pracy w widocznym zakresie (tylko aktywne)">
                  dni
                </th>
              </tr>
            </thead>
            {/* owner 2026-10-26 07:49: the events strip — a bar per event over its days */}
            <tbody className="pg-strip" aria-label="Wydarzenia" data-feature-id="crew.grid_staffing">
              <tr>
                <th scope="row" className="people-grid-person pg-strip-title" colSpan={days.length + 2}>
                  <span className="pg-strip-label">
                    Wydarzenia
                    {strip.data && bars.length === 0 && <span className="muted"> — {onlyMissing ? 'brak wydarzeń z brakami' : 'brak wydarzeń w tym zakresie'}</span>}
                  </span>
                </th>
              </tr>
              {/* owner 2026-10-27 21:04: as few rows as possible — a new row only where bars overlap */}
              {packBars(bars).map((row, r) => {
                let at = 0;
                const cells = row.flatMap(({ e, cols, key }) => {
                  const gap = cols.before - at;
                  at = cols.before + cols.span;
                  return [
                    ...(gap > 0 ? [<td key={`${key}-gap`} colSpan={gap} />] : []),
                    <td key={key} colSpan={cols.span} className="pg-bar-cell" {...pastStyle(barPast(cols, days, today))}>
                      <EventBar event={e} chosen={!!e.eventId && e.eventId === chosenEvent} onChoose={choose} />
                    </td>,
                  ];
                });
                return (
                  <tr key={row[0]!.key ?? r}>
                    <th className="people-grid-person" aria-hidden="true" />
                    {cells}
                    {days.length - at > 0 && <td colSpan={days.length - at} />}
                    <td />
                  </tr>
                );
              })}
            </tbody>
            {/* owner 2026-10-27 08:21–08:22: the „Brakuje” row — no label; per day the people missing in every event */}
            {strip.data?.missing && (
              <tbody className="pg-missing" aria-label="Brakuje" data-feature-id="crew.grid_staffing">
                <tr>
                  <th scope="row" className="people-grid-person" />
                  {days.map((d) => (
                    <td key={d} className={`pg-miss-cell${d < today ? ' pg-past' : ''}`}>
                      {missing.has(d) && <MissingTile day={missing.get(d)!} open={missOpen === d} onOpen={(open) => setMissOpen(open ? d : null)} onChoose={(id) => choose(id)} />}
                    </td>
                  ))}
                  <td className="pg-load" />
                </tr>
              </tbody>
            )}
            <tbody>
              {grid.data.people.map((p) => (
                <tr key={p.id}>
                  <th scope="row" className="people-grid-person" onMouseEnter={marqueeOn} onMouseLeave={marqueeOff}>
                    <Clip>{nameOf(p)}</Clip>
                  </th>
                  {days.map((d) => (
                    <GridCellView key={d} cell={cells.get(cellKey(p.id, d))} personName={nameOf(p)} day={d} past={d < today} selected={selected.has(cellKey(p.id, d))} selectable={mayEdit || mayAsk || mayStaff || mayForce} open={menu?.personId === p.id && menu.day === d} onPick={(how, anchor) => pick(p.id, d, how, anchor)} />
                  ))}
                  <td className="pg-load" aria-label={`${nameOf(p)}: dni pracy ${p.workDays ?? 0}`}>
                    {p.workDays ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sideEvent && <EventPanel key={sideEvent} eventId={sideEvent} onClose={() => choose(null)} />}
      </div>
      {grid.data && grid.data.people.length > 0 && <Legend />}
      {menu && grid.data && (
        <CellMenu
          anchor={menu.anchor}
          head={multi ? `Zaznaczone: ${cellsCount(selected.size)}` : `${nameOf(grid.data.people.find((p) => p.id === menu.personId) ?? {})} · ${shortDate(menu.day).slice(0, -5)}`}
          {...(!multi && menu.day < today ? { note: PAST_DAY_NOTE } : {})}
          {...(menuEvent && !multi ? { event: menuEvent } : {})}
          actions={multi ? multiActions({ staff: mayStaff, edit: mayEdit, force: mayForce, ask: mayAsk, confirm: mayForce }, selected.size) : cellActions(menuCell, menu.day, today, { staff: mayStaff, edit: mayEdit, force: mayForce, ask: mayAsk, release: mayRelease, confirm: mayForce })}
          busy={mark.isPending || force.isPending || runBatch.isPending || dismissAssign.isPending || quickBusy}
          onPick={act}
          {...(quick ? { choices: { after: 'fill' as const, label: 'Wybierz stanowisko', items: quick.choices.map((c) => ({ key: choiceKey(c), label: choiceLabel(c) })), onPick: (key: string) => {
            const c = quick.choices.find((x) => choiceKey(x) === key);
            if (!c) return;
            setMenu(null);
            place(c, quick.cells, quick.keep);
          } } } : {})}
          onClose={closeMenu}
        />
      )}
      {mayQuestions && <PeopleQuestions from={from} to={to} />}
    </div>
  );
}

function GridCellView({ cell, personName, day, past, selected, selectable, open, onPick }: { cell: GridCell | undefined; personName: string; day: string; past: boolean; selected: boolean; selectable: boolean; open: boolean; onPick: (how: { shift: boolean; add: boolean }, anchor: HTMLElement) => void }) {
  const view = cellView(cell);
  const status = LEGEND.find((l) => l.kind === view.kind)?.label;
  return (
    <td
      className={`pg-cell${past ? ' pg-past' : ''}${selected ? ' selected' : ''}`}
      title={view.title || undefined}
      aria-label={`${personName}, ${shortDate(day)}${status ? ` — ${status}` : ''}${view.title ? `: ${view.title.replaceAll('\n', '; ')}` : ''}`}
      aria-haspopup="menu"
      aria-expanded={open}
      {...(selectable ? { 'aria-selected': selected } : {})}
      tabIndex={0}
      // owner 2026-10-27 08:14: Alt / Option-click chooses a cell (Safari on a Mac: no text selection, no download)
      onMouseDown={(e) => {
        if (e.altKey || e.shiftKey || e.metaKey || e.ctrlKey) e.preventDefault();
      }}
      onMouseEnter={marqueeOn}
      onMouseLeave={marqueeOff}
      onClick={(e) => onPick({ shift: e.shiftKey, add: e.ctrlKey || e.metaKey || e.altKey }, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || (e.key !== ' ' && e.key !== 'Enter')) return;
        e.preventDefault();
        onPick({ shift: e.shiftKey, add: e.ctrlKey || e.metaKey || e.altKey }, e.currentTarget);
      }}
    >
      {view.kind !== 'empty' && (
        <div className={`pg-chip ${view.kind}${view.marker ? ' pg-marker' : ''}`} data-status={view.kind}>
          {view.marker ? (
            <span className="pg-letter">{view.label}</span>
          ) : (
            <>
              <Clip className="pg-chip-label">{view.label}</Clip>
              {view.assignments
                .filter((a) => a.active !== false && a.functionName && view.kind !== 'collision')
                .slice(0, 1)
                .map((a, i) => (
                  <Clip key={i} className="pg-fn">
                    {a.functionName}
                  </Clip>
                ))}
            </>
          )}
        </div>
      )}
    </td>
  );
}

/**
 * Owner 2026-10-26 07:49: a bar of the events strip. With numbers (the crew may be seen) it chooses the event and opens
 * its side panel (again: closes it); without numbers it links to the event's „Obsada”; an event the user may not see
 * is plain text.
 */
function EventBar({ event: e, chosen, onChoose }: { event: StripEvent; chosen: boolean; onChoose: (eventId: string | null) => void }) {
  const tone = barTone(e);
  // owner 2026-10-27 08:16: the bar says the name only; the numbers are in its hover bubble (and the side panel's ⓘ Info)
  const numbers = barText(e);
  const label = `${e.eventName}${tone === 'ok' ? ' — obsada pełna i potwierdzona' : tone === 'missing' ? ' — braki w obsadzie' : ''}`;
  const body = (
    <Clip>
      <strong>{e.eventName}</strong>
    </Clip>
  );
  return (
    <Bubble lines={numbers ? [numbers] : []}>
      {(describedBy) =>
        e.eventId && e.functions ? (
          <button className={`pg-bar ${tone}${chosen ? ' chosen' : ''}`} type="button" aria-label={label} aria-describedby={describedBy} aria-pressed={chosen} data-status={tone} onClick={() => onChoose(chosen ? null : e.eventId!)}>
            {body}
          </button>
        ) : e.eventId ? (
          <Link className={`pg-bar ${tone}`} to={crewLink(e.eventId)} aria-label={label} aria-describedby={describedBy} data-status={tone}>
            {body}
          </Link>
        ) : (
          <span className={`pg-bar ${tone}`} data-status={tone}>
            {body}
          </span>
        )
      }
    </Bubble>
  );
}

/** The statuses of a cell, under the grid. */
export function Legend() {
  return (
    <ul className="pg-legend" aria-label="Legenda">
      {LEGEND.map((l) => (
        <li key={l.kind}>
          {/* owner 2026-10-27 08:14: D and N on their colours, as in the tiles */}
          <i className={`pg-swatch ${l.kind}`} aria-hidden="true">
            {l.letter}
          </i>
          {l.label}
        </li>
      ))}
      {/* owner 2026-10-27 23:31: past days are greyed */}
      <li>
        <i className="pg-swatch pg-past" aria-hidden="true" />
        miniony dzień
      </li>
    </ul>
  );
}

/** Owner 2026-10-27 23:31: a bar greyed over its past days only — the share as `--pg-past` (none: no style). */
function pastStyle(share: number): { style?: React.CSSProperties } {
  return share > 0 ? { style: { '--pg-past': `${Math.round(share * 100)}%` } as React.CSSProperties } : {};
}

/** „Osoby”: choose people by hand (none chosen = everyone). */
function PeopleChooser({ people, chosen, onChange }: { people: Array<{ id: string; name?: string; functionLabel?: string }>; chosen: string[]; onChange: (ids: string[]) => void }) {
  const set = new Set(chosen);
  return (
    <details className="people-chooser">
      <summary className="btn ghost small">{chosen.length === 0 ? 'Osoby: wszystkie' : `Osoby: ${chosen.length}`}</summary>
      <div className="people-chooser-list" role="group" aria-label="Wybierz osoby">
        {chosen.length > 0 && (
          <button className="btn ghost small" type="button" onClick={() => onChange([])}>
            Pokaż wszystkich
          </button>
        )}
        {people.map((p) => (
          <label key={p.id} className="check">
            <input
              type="checkbox"
              checked={set.has(p.id)}
              onChange={(e) => onChange(e.target.checked ? [...chosen, p.id] : chosen.filter((id) => id !== p.id))}
            />
            {personLabel(p)}
          </label>
        ))}
      </div>
    </details>
  );
}
